import {default as Image} from '@/components/global/images/resized-cdn-image';
import { useParams } from 'next/navigation';
import classnames from 'classnames';
import React, { ReactElement, useEffect, useId, useRef } from 'react';

import { doTranslation } from '@/app/i18n/client';
import { CarrierImage } from '@/utils/carriers';
import CarrierLogo from '@/components/global/carrier-logo/carrier-logo';
import { StrapiCarrier } from '@/apis/strapi';

import closeDark from '@/public/assets/images/global/close-dark.svg';

import styles from '@/components/global/modal/modal.module.scss';

import dynamic from 'next/dynamic';
const RatingStars = dynamic(() => import('@/components/schedule-results/rating-stars'), {
  ssr: false
});

interface Props extends React.HTMLAttributes<HTMLDivElement> {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  header?: ReactElement;
  size?: string;
  carrier?: StrapiCarrier;
  carrierImages?: { [key: string]: CarrierImage };
  isCartTimeoutRelated?: boolean;
}

const Modal = (props: Props): React.JSX.Element | null => {
  const id = useId();
  const { lng } = useParams() as { lng: string };
  const ref = useRef<HTMLDivElement>(null);
  const { t } = doTranslation(lng);

  useEffect(() => {
    if (ref?.current) {
      const handleClick = (event: any) => {
        if (ref.current === event.target) {
          props.onClose();
        }
      };

      const handleKeydown = (event: any) => {
        if (event.key === 'Escape') {
          props.onClose();
        }
        const focusableModalElements = ref?.current?.querySelectorAll(
          'button, input, select, textarea, [href], [tabindex]:not([tabindex="-1"])'
        );
        const firstElement = focusableModalElements?.length ? focusableModalElements[0] : null;
        const lastElement = focusableModalElements?.length
          ? focusableModalElements[focusableModalElements.length - 1]
          : null;
        const titleElement = document.querySelector(`.${styles.title}`);
        if (event.key === 'Tab') {
          if (event.shiftKey && document.activeElement === firstElement) {
            event.preventDefault();
            (lastElement as HTMLElement)?.focus();
          } else if (!event.shiftKey && document.activeElement === lastElement) {
            event.preventDefault();
            (titleElement as HTMLElement)?.focus();
          }
        }
      };

      const focusables = document?.getElementsByClassName(styles.title);
      if (focusables.length) {
        const focusable = focusables[0] as HTMLElement;
        focusable?.setAttribute('tabindex', '0');
        focusable?.focus();
      }

      document.addEventListener('keydown', handleKeydown);
      document.addEventListener('click', handleClick, true);
      return () => {
        document.removeEventListener('click', handleClick, true);
        document.removeEventListener('click', handleKeydown, true);
      };
    }
  }, [props, id]);

  useEffect(() => {
    if (props.isOpen && props.isCartTimeoutRelated) {
      ref.current?.setAttribute('aria-hidden', 'false');
      ref.current?.focus();
    }
  }, [props.isCartTimeoutRelated, props.isOpen]);

  if (!props.isOpen) {
    return null;
  }

  return (
    <div
      aria-atomic={props.isCartTimeoutRelated ? 'true' : 'false'}
      aria-describedby={`modal-${id}-details`}
      aria-labelledby={`modal-${id}-labelledby`}
      aria-modal="true"
      className={styles.modal}
      ref={ref}
      role={props.isCartTimeoutRelated ? 'alert' : 'dialog'}
    >
      <div
        className={classnames({
          [styles.container]: true,
          [styles.small]: props.size === 'small',
          [styles.large]: props.size === 'large'
        })}
      >
        <div className={styles.top}>
          <h2 className={styles.title} id={`modal-${id}-labelledby`}>
            {props.carrier && props.carrierImages && props.carrierImages[props.carrier.attributes.carrier_id] && (
              <CarrierLogo carrierImage={props.carrierImages[props.carrier.attributes.carrier_id]} />
            )}
            {props.title}
          </h2>
          <div
            className={classnames({
              [styles.close]: true,
              [styles.block]: !props.carrier ? true : false
            })}
          >
            {props.carrier && (
              <div className={styles.rating}>
                <RatingStars
                  score={props.carrier.attributes.aggregate_rating || 0}
                  hasRatings={props.carrier.attributes.rating_count > 0}
                />
                <div className={styles.text} aria-hidden={props.carrier.attributes.rating_count > 0 ? undefined : true}>
                  {props.carrier.attributes.rating_count > 0
                    ? t('{{ rating }} RATINGS', { rating: props.carrier.attributes.rating_count })
                    : t('Not yet rated')}
                </div>
              </div>
            )}
            <button className={styles.button} onClick={() => props.onClose()} type="button">
              <Image alt={t('Close')} className={styles.img} height="26" src={closeDark} width="26" />
            </button>
          </div>
        </div>
        <div>{props.header}</div>
        <div className={styles.bottom} id={`modal-${id}-details`}>
          {props.children}
        </div>
      </div>
    </div>
  );
};

export default Modal;
